package io.horion.cv.components.preview

import androidx.compose.runtime.Composable
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.api.datasource.cv.Reference
import org.jetbrains.compose.web.dom.*

private const val C5_ACCENT       = "#3b5ea6"
private const val C5_BLUE         = "#1D3967"
private const val C5_DarkBLUE     = "#687EB6"
private const val C5_LIGHTBLUE    = "#748CB7"
private const val C5_SECTION_LINE = "#3a4a6a"
private const val C5_WHITE        = "#ffffff"
private const val C5_TEXT         = "#222222"
private const val C5_GRAY_TEXT    = "#555555"
private const val C5_LABEL        = "#cccccc"
private const val C5_COMPANY      = "#4a90d9"
private const val C5_CITY         = "#666666"
private const val C5_BORDER       = "#dddddd"
private const val C5_LANG_TRACK   = "#2a3a5a"
private const val C5_BULLET       = "#4a90d9"
private const val BG_TEMPLATE_5   = "/cv5-bg.png"

private const val C5_HEADER_HEIGHT = "192px"
private const val C5_SIDEBAR_GAP   = "74px"
private const val C5_PHOTO_LEFT    = "30px"
private const val C5_PHOTO_TOP     = "92px"
private const val C5_PHOTO_WIDTH   = "165px"
private const val C5_PHOTO_HEIGHT  = "172px"

@Composable
fun CvFivePreview(data: OrionCV) {
    val pi = data.personalInformation
    val validPersonalizedSections =
        data.personalizedSections.filter { section ->
            section.resume.isNotBlank() ||
                    section.started.isNotBlank() ||
                    section.ended.isNotBlank() ||
                    section.description.isNotBlank()
        }

    Div(attrs = {
        style {
            property("display", "flex")
            property("width", "794px")
            property("min-height", "1123px")
            property("gap", "25px")
            property("font-family", "Inter, 'Segoe UI', Arial, sans-serif")
            property("font-size", "11px")
            property("color", C5_TEXT)
            property("box-sizing", "border-box")
            property("overflow", "hidden")
            property("position", "relative")
            property("background-image", "url('$BG_TEMPLATE_5')")
            property("background-size", "794px 1123px")
            property("background-repeat", "no-repeat")
            property("background-position", "top left")
        }
    }) {

        // ── SIDEBAR ────────────────────────────────────────────────────────────
        Div(attrs = {
            style {
                property("width", "271px")
                property("padding-left", "30px")
                property("min-height", "1123px")
                property("color", C5_WHITE)
                property("flex-shrink", "0")
                property("min-width", "0")
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
            }
        }) {
            Div(attrs = { style {
                property("padding", "22px 16px 18px 16px")
                property("min-height", C5_HEADER_HEIGHT)
                property("box-sizing", "border-box")
            }}) {
                val hasName = pi.firstName.isNotBlank() || pi.lastName.isNotBlank()
                if (hasName) {
                    Div(attrs = { style {
                        property("color", C5_WHITE)
                        property("font-size", "13px")
                        property("font-weight", "700")
                        property("text-transform", "uppercase")
                        property("line-height", "1.3")
                        property("letter-spacing", "0.5px")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }}) { Text("${pi.firstName} ${pi.lastName}".trim()) }
                }
                if (pi.bp.isNotBlank()) {
                    Div(attrs = { style {
                        property("color", C5_WHITE)
                        property("font-size", "13px")
                        property("text-transform", "uppercase")
                        property("letter-spacing", "1px")
                        property("margin-top", "3px")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }}) { Text(pi.bp) }
                }
            }

            Div(attrs = { style {
                property("height", C5_SIDEBAR_GAP)
                property("flex-shrink", "0")
            }})

            Div(attrs = { style {
                property("padding", "0 16px 20px 16px")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "4px")
                property("min-width", "0")
            }}) {

                val hasBirth   = pi.birth.isNotBlank()
                val hasNat     = pi.nationality.isNotBlank()
                val hasMarital = pi.maritalStatus.isNotBlank()
                val hasLicense = pi.drivingLicense.isNotBlank()
                if (hasBirth || hasNat || hasMarital || hasLicense) {
                    Sb5Title("INFORMATIONS")
                    if (hasBirth)   Sb5InfoRow("Né le :", pi.birth)
                    if (hasNat)     Sb5InfoRow("Nationalité :", pi.nationality)
                    if (hasMarital) Sb5InfoRow("Sit. fam. :", pi.maritalStatus)
                    if (hasLicense) Sb5InfoRow("Permis :", pi.drivingLicense)
                }

                val validFormations = data.formations.filter { it.label.isNotBlank() || it.school.isNotBlank() }
                if (validFormations.isNotEmpty()) {
                    Sb5Title("EDUCATION")
                    validFormations.forEach { f ->
                        val period = formatPeriod(f.started, f.ended)
                        Div(attrs = { style {
                            property("margin-bottom", "8px")
                            property("min-width", "0")
                        }}) {
                            Div(attrs = { style {
                                property("color", C5_WHITE)
                                property("font-size", "10.5px")
                                property("font-weight", "700")
                                property("font-style", "italic")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }}) { Text(f.label) }
                            Div(attrs = { style {
                                property("color", C5_LABEL)
                                property("font-size", "9.5px")
                                property("margin-top", "1px")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }}) {
                                val school = f.school
                                Text("$school${if (period.isNotBlank()) " [$period]" else ""}")
                            }
                            if (f.description.isNotBlank()) {
                                Div(attrs = { style {
                                    property("color", C5_LABEL)
                                    property("font-size", "9.5px")
                                    property("margin-top", "2px")
                                    property("overflow-wrap", "anywhere")
                                    property("word-break", "break-word")
                                }}) { HtmlInline(f.description) }
                            }
                        }
                    }
                }

                val validQualities = data.qualities.filter { it.isNotBlank() }
                if (validQualities.isNotEmpty()) {
                    Sb5Title("QUALITÉS")
                    validQualities.forEach { quality -> Sb5BulletRow(quality) }
                }

                val validCompetences = data.competences.filter { it.name.isNotBlank() }
                if (validCompetences.isNotEmpty()) {
                    Sb5Title("COMPETENCES")
                    validCompetences.forEach { comp -> Sb5BulletRow(comp.name) }
                }

                val validLanguages = data.languages.filter { it.language.name.isNotBlank() }

                if (validLanguages.isNotEmpty()) {
                    Sb5Title("LANGUES")

                    validLanguages.forEach { lang ->

                        Div(attrs = {
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "10px")
                                property("margin-bottom", "8px")
                                property("min-width", "0")
                            }
                        }) {
                            Div(attrs = {
                                style {
                                    property("color", C5_WHITE)
                                    property("font-size", "10px")
                                    property("font-weight", "700")
                                    property("letter-spacing", "1px")
                                    property("width", "65px")
                                    property("flex-shrink", "0")

                                    property("overflow-wrap", "anywhere")
                                    property("word-break", "break-word")
                                }
                            }) {
                                Text(lang.language.name)
                            }

                            Div(attrs = {
                                style {
                                    property("background", C5_WHITE)
                                    property("height", "10px")
                                    property("flex", "1")
                                    property("min-width", "0")
                                }
                            }) {
                                Div(attrs = {
                                    style {
                                        property("background", C5_DarkBLUE)
                                        property("height", "10px")
                                        property(
                                            "width",
                                            "${(lang.level.num * 100).toInt().coerceIn(0, 100)}%"
                                        )
                                    }
                                })
                            }
                        }
                    }
                }

                val validHobbies = data.hobbies.filter { it.isNotBlank() }
                if (validHobbies.isNotEmpty()) {
                    Sb5Title("HOBBIES")
                    Div(attrs = { style {
                        property("display", "grid")
                        property("grid-template-columns", "1fr 1fr")
                        property("gap", "4px 8px")
                        property("margin-top", "4px")
                    }}) {
                        validHobbies.forEach { hobby ->
                            Div(attrs = { style {
                                property("color", C5_LABEL)
                                property("font-size", "10px")
                                property("text-align", "center")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }}) { Text(hobby) }
                        }
                    }
                }
            }
        }

        // ── CONTENU PRINCIPAL ──────────────────────────────────────────────────
        Div(attrs = {
            style {
                property("flex", "1")
                property("min-width", "0")
                property("padding", "0 0 24px 0")
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
            }
        }) {

            Div(attrs = { style {
                property("padding", "20px 24px 22px 24px")
                property("min-height", C5_HEADER_HEIGHT)
                property("box-sizing", "border-box")
            }}) {
                if (!data.profil.isNullOrBlank()) {

                    Div(attrs = { style {
                        property("display", "block")

                        property("border-top", "1px solid $C5_WHITE")
                        property("border-right", "1px solid $C5_WHITE")
                        property("border-bottom", "1px solid $C5_WHITE")
                        property("color", C5_WHITE)
                        property("font-size", "13px")
                        property("font-weight", "700")
                        property("text-transform", "uppercase")
                        property("letter-spacing", "1px")
                        property("padding", "6px 16px 6px 0px")
                        property("margin-bottom", "12px")
                        property("box-sizing", "border-box")
                    }}) {
                        Text("A PROPOS")
                    }

                    HtmlBlock(data.profil) {
                        property("font-size", "11px")
                        property("color", C5_WHITE)
                        property("line-height", "1.65")
                        property("text-align", "justify")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
                    }
                }
            }

            val hasPhone   = pi.phone.isNotBlank()
            val hasEmail   = pi.email.isNotBlank()
            val hasAddress = pi.address.isNotBlank()
            val hasWebsite = pi.siteWeb?.second?.isNotBlank() == true
            val hasLinks   = pi.links.any { it.second.isNotBlank() }
            if (hasPhone || hasEmail || hasAddress || hasWebsite || hasLinks) {
                Main5Section("CONTACT")
                Div(attrs = { style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "16px")
                    property("padding", "10px 20px 14px 20px")
                    property("align-items", "flex-start")
                }}) {
                    if (hasPhone) {
                        Div(attrs = { style {
                            property("font-size", "10.5px")
                            property("color", C5_LIGHTBLUE)
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }}) { Text(pi.phone) }
                    }
                    if (hasEmail) {
                        Div(attrs = { style {
                            property("font-size", "10.5px")
                            property("color", C5_LIGHTBLUE)
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }}) { Text(pi.email) }
                    }
                    pi.siteWeb?.let { (_, url) ->
                        if (url.isNotBlank()) {
                            A(href = url, attrs = {
                                attr("target", "_blank"); attr("rel", "noopener noreferrer")
                                style {
                                    property("font-size", "10.5px"); property("color", C5_LIGHTBLUE)
                                    property("overflow-wrap", "anywhere"); property("word-break", "break-word")
                                }
                            }) { Text(url) }
                        }
                    }
                    pi.links.forEach { (_, url) ->
                        if (url.isNotBlank()) {
                            A(href = url, attrs = {
                                attr("target", "_blank"); attr("rel", "noopener noreferrer")
                                style {
                                    property("font-size", "10.5px"); property("color", C5_LIGHTBLUE)
                                    property("overflow-wrap", "anywhere"); property("word-break", "break-word")
                                }
                            }) { Text(url) }
                        }
                    }
                    if (hasAddress) {
                        Div(attrs = { style {
                            property("font-size", "10.5px")
                            property("color", C5_LIGHTBLUE)
                            property("text-align", "right")
                            property("flex", "1")
                            property("min-width", "0")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }}) { Text(pi.address) }
                    }
                }
            }

            // ── RUBRIQUES PERSONNALISÉES ──────────────────────────────────────────────
            if (validPersonalizedSections.isNotEmpty()) {
                validPersonalizedSections.forEach { section ->
                    Cv5PersonalizedSection(section)
                }
            }

            val validExperiences = data.experiences.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
            if (validExperiences.isNotEmpty()) {
                Main5Section("EXPERIENCES")
                Div(attrs = { style {
                    property("padding", "8px 20px 4px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "14px")
                }}) {
                    validExperiences.forEach { exp ->
                        Cv5TimelineItem(
                            title    = exp.post,
                            company  = exp.enterprise,
                            city     = exp.city,
                            period   = formatPeriod(exp.started, exp.ended),
                            bullets  = exp.description
                        )
                    }
                }
            }

            val validStages = data.stages.filter { it.post.isNotBlank() || it.enterprise.isNotBlank() }
            if (validStages.isNotEmpty()) {
                Main5Section("STAGES")
                Div(attrs = { style {
                    property("padding", "8px 20px 4px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "14px")
                }}) {
                    validStages.forEach { stage ->
                        Cv5TimelineItem(
                            title    = stage.post,
                            company  = stage.enterprise,
                            city     = stage.city,
                            period   = formatPeriod(stage.started, stage.ended),
                            bullets  = if (stage.description.isNotBlank()) listOf(stage.description) else emptyList()
                        )
                    }
                }
            }

            val validLearns = data.learns.filter { it.label.isNotBlank() }
            if (validLearns.isNotEmpty()) {
                Main5Section("COURS")
                Div(attrs = { style {
                    property("padding", "8px 20px 4px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "14px")
                }}) {
                    validLearns.forEach { learn ->
                        Cv5TimelineItem(
                            title    = learn.label,
                            company  = "",
                            city     = "",
                            period   = formatPeriod(learn.started, learn.ended),
                            bullets  = if (learn.description.isNotBlank()) listOf(learn.description) else emptyList()
                        )
                    }
                }
            }

            val validActivities = data.extracurricularActivities.filter { it.name.isNotBlank() }
            if (validActivities.isNotEmpty()) {
                Main5Section("ACTIVITÉS EXTRA-SCOLAIRES")
                Div(attrs = { style {
                    property("padding", "8px 20px 4px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "14px")
                }}) {
                    validActivities.forEach { activity ->
                        Cv5TimelineItem(
                            title    = activity.name,
                            company  = activity.enterprise,
                            city     = activity.city,
                            period   = formatPeriod(activity.started, activity.ended),
                            bullets  = if (activity.description.isNotBlank()) listOf(activity.description) else emptyList()
                        )
                    }
                }
            }

            val validCertificates = data.certificates.filter { it.name.isNotBlank() }
            if (validCertificates.isNotEmpty()) {
                Main5Section("CERTIFICATS")
                Div(attrs = { style {
                    property("padding", "8px 20px 4px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "14px")
                }}) {
                    validCertificates.forEach { cert ->
                        Cv5TimelineItem(
                            title    = cert.name,
                            company  = "",
                            city     = "",
                            period   = extractYear(cert.date),
                            bullets  = if (cert.description.isNotBlank()) listOf(cert.description) else emptyList()
                        )
                    }
                }
            }

            val validAchievements = data.achievements.filter { it.isNotBlank() }
            if (validAchievements.isNotEmpty()) {
                Main5Section("RÉALISATIONS")
                Div(attrs = { style {
                    property("padding", "10px 20px 8px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "4px")
                }}) {
                    validAchievements.forEach { item ->
                        Div(attrs = { style {
                            property("display", "flex"); property("gap", "5px")
                            property("font-size", "10px"); property("color", C5_GRAY_TEXT)
                            property("min-width", "0")
                        } }) {
                            Span(attrs = { style { property("flex-shrink", "0") } }) { Text("•") }
                            Div(attrs = { style {
                                property("min-width", "0")
                                property("overflow-wrap", "anywhere")
                                property("word-break", "break-word")
                            }}) { HtmlInline(item) }
                        }
                    }
                }
            }

            val validReferences = data.references.filter { it.name.isNotBlank() }
            if (validReferences.isNotEmpty()) {
                Main5Section("RÉFÉRENCES")
                Div(attrs = { style {
                    property("padding", "10px 20px 8px 20px")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "8px")
                }}) {
                    validReferences.forEach { ref -> Cv5ReferenceCard(ref) }
                }
            }

            data.signature?.let { sig ->
                if (sig.city.isNotBlank() || sig.date.isNotBlank() || sig.image.isNotBlank()) {
                    Div(attrs = { style {
                        property("padding", "14px 20px 0 20px")
                        property("display", "flex")
                        property("justify-content", "flex-end")
                    }}) {
                        Div(attrs = { style { property("text-align", "right") } }) {
                            val line = listOfNotNull(sig.city.takeIf { it.isNotBlank() }, sig.date.takeIf { it.isNotBlank() })
                            if (line.isNotEmpty()) {
                                Div(attrs = { style { property("font-size", "10px"); property("color", C5_GRAY_TEXT) } }) { Text(line.joinToString(", ")) }
                            }
                            if (sig.image.isNotBlank()) {
                                Img(src = sig.image, attrs = { style { property("height", "50px"); property("margin-top", "4px") } })
                            }
                        }
                    }
                }
            }

            val validSoftware = data.software.filter { it.name.isNotBlank() }
            if (validSoftware.isNotEmpty()) {
                Main5Section("LOGICIELS")
                Div(attrs = { style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "8px 0")
                    property("padding", "10px 20px 8px 20px")
                }}) {
                    validSoftware.forEach { sw ->
                        Div(attrs = { style {
                            property("flex", "0 0 33%")
                            property("font-size", "10.5px")
                            property("color", C5_GRAY_TEXT)
                            property("text-align", "center")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }}) { Text(sw.name) }
                    }
                }
            }
        }

        // ── PHOTO (position absolue, calée sur le cadre de l'image de fond) ─────
        if (pi.photo.isNotBlank()) {
            Div(attrs = { style {
                property("position", "absolute")
                property("left", C5_PHOTO_LEFT)
                property("top", C5_PHOTO_TOP)
                property("width", C5_PHOTO_WIDTH)
                property("height", C5_PHOTO_HEIGHT)
                property("overflow", "hidden")
            }}) {
                Img(src = pi.photo, attrs = { style {
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "cover")
                }})
            }
        }
    }
}

@Composable
private fun Sb5Title(title: String) {
    Div(attrs = { style {
        property("color", C5_WHITE)
        property("font-size", "12px")
        property("font-weight", "800")
        property("text-transform", "uppercase")
        property("letter-spacing", "0.8px")
        property("border-bottom", "1px solid $C5_WHITE")
        property("padding-bottom", "4px")
        property("margin-top", "14px")
        property("margin-bottom", "7px")
    }}) { Text(title) }
}

@Composable
private fun Sb5InfoRow(label: String, value: String) {
    Div(attrs = { style {
        property("display", "flex")
        property("gap", "6px")
        property("font-size", "10px")
        property("margin-bottom", "4px")
        property("align-items", "flex-start")
        property("min-width", "0")
    }}) {
        Span(attrs = { style {
            property("color", C5_WHITE)
            property("flex-shrink", "0")
            property("font-weight", "500")
        }}) { Text(label) }
        Span(attrs = { style {
            property("color", C5_LABEL)
            property("min-width", "0")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }}) { Text(value) }
    }
}

@Composable
private fun Sb5BulletRow(text: String) {
    Div(attrs = { style {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "6px")
        property("margin-bottom", "4px")
        property("min-width", "0")
    }}) {
        Span(attrs = { style {
            property("color", C5_LABEL)
            property("font-size", "10px")
            property("line-height", "1")
            property("flex-shrink", "0")
        }}) { Text("•") }
        Span(attrs = { style {
            property("color", C5_WHITE)
            property("font-size", "10.5px")
            property("min-width", "0")
            property("overflow-wrap", "anywhere")
            property("word-break", "break-word")
        }}) { Text(text) }
    }
}


@Composable
private fun Main5Section(title: String) {
    Div(attrs = { style {
        property("border-top", "1px solid $C5_BLUE")
        property("border-right", "1px solid $C5_BLUE")
        property("border-bottom", "1px solid $C5_BLUE")
        property("margin-right", "20px")
        property("padding", "4px 16px 4px 0px")
        property("font-size", "14px")
        property("font-weight", "800")
        property("color", C5_BLUE)
        property("text-transform", "uppercase")
        property("letter-spacing", "1px")
        property("box-sizing", "border-box")
    }}) { Text(title) }
}

@Composable
private fun Cv5TimelineItem(
    title:   String,
    company: String,
    city:    String,
    period:  String,
    bullets: List<String>
) {
    Div(attrs = { style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "2px")
        property("min-width", "0")
    }}) {
        Div(attrs = { style {
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "baseline")
            property("gap", "8px")
        }}) {
            Div(attrs = { style {
                property("font-size", "13px")
                property("font-weight", "700")
                property("color", C5_TEXT)
                property("text-transform", "uppercase")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
                property("flex", "1")
                property("min-width", "0")
            }}) { Text(title) }
            Div(attrs = { style {
                property("font-size", "10px")
                property("color", C5_BLUE)
                property("font-weight", "700")
                property("white-space", "nowrap")
                property("flex-shrink", "0")
            }}) { Text(period) }
        }

        if (company.isNotBlank()) {
            Div(attrs = { style {
                property("font-size", "11px")
                property("color", C5_LIGHTBLUE)
                property("font-weight", "600")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }}) { Text(company) }
        }

        if (city.isNotBlank()) {
            Div(attrs = { style {
                property("font-size", "10px")
                property("color", C5_CITY)
                property("font-style", "italic")
                property("margin-bottom", "3px")
                property("overflow-wrap", "anywhere")
                property("word-break", "break-word")
            }}) { Text(city) }
        }

        if (bullets.isNotEmpty()) {
            Div(attrs = { style {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "2px")
            }}) {
                bullets.forEach { b ->
                    Div(attrs = { style {
                        property("display", "flex")
                        property("gap", "5px")
                        property("font-size", "10px")
                        property("color", C5_GRAY_TEXT)
                        property("min-width", "0")
                    }}) {
                        Span(attrs = { style {
                            property("flex-shrink", "0")
                            property("margin-top", "1px")
                        }}) { Text("•") }
                        Div(attrs = { style {
                            property("min-width", "0")
                            property("overflow-wrap", "anywhere")
                            property("word-break", "break-word")
                        }}) { HtmlInline(b) }
                    }
                }
            }
        }
    }
}

@Composable
private fun Cv5ReferenceCard(ref: Reference) {
    Div(
        attrs = {
            style {
                property("border-bottom", "1px solid $C5_BORDER");
                property("padding-bottom", "6px") }
        }) {
        Div(
            attrs = {
                style {
                    property("font-weight", "700");
                    property("font-size", "11px");
                    property("color", C5_TEXT)
                    property("overflow-wrap", "anywhere");
                    property("word-break", "break-word")
        } }
        ) { Text(ref.name) }
        val subtitleParts = listOfNotNull(ref.enterprise.takeIf { it.isNotBlank() }, ref.city.takeIf { it.isNotBlank() })
        if (subtitleParts.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        property("font-size", "10px");
                        property("color", C5_COMPANY);
                        property("margin-top", "2px")
                        property("overflow-wrap", "anywhere")
                        property("word-break", "break-word")
            } }) {
                Text(subtitleParts.joinToString(" · ")
                ) }
        }
        val contactParts = listOfNotNull(ref.phone.takeIf { it.isNotBlank() }, ref.email.takeIf { it.isNotBlank() })
        if (contactParts.isNotEmpty()) {
            Div(
                attrs = {
                    style {
                        property("font-size", "10px");
                        property("color", C5_GRAY_TEXT);
                        property("margin-top", "3px")
                        property("overflow-wrap", "anywhere");
                        property("word-break", "break-word")
            } }) { Text(contactParts.joinToString("   •   ")) }
        }
    }
}

@Composable
private fun Cv5PersonalizedSection(
    section: io.horion.cv.api.datasource.cv.Section
) {
    val sectionTitle = section.title
        .ifBlank { "RUBRIQUE PERSONNALISÉE" }

    Main5Section(sectionTitle)

    Div(attrs = {
        style {
            property("width", "100%")
            property("max-width", "100%")
            property("min-width", "0")
            property("box-sizing", "border-box")
            property("padding", "10px 20px 8px 20px")
            property("margin-right", "20px")
            property("overflow", "hidden")
        }
    }) {
        if (section.resume.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "11px")
                    property("font-weight", "700")
                    property("color", C5_TEXT)
                    property("line-height", "1.4")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(section.resume)
            }
        }

        if (
            section.started.isNotBlank() ||
            section.ended.isNotBlank()
        ) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", C5_CITY)
                    property("line-height", "1.3")
                    property("margin-top", "3px")
                    property("margin-bottom", "4px")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }) {
                Text(
                    listOf(
                        section.started,
                        section.ended
                    )
                        .filter { it.isNotBlank() }
                        .joinToString(" — ")
                )
            }
        }

        if (section.description.isNotBlank()) {
            Div(attrs = {
                style {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", C5_GRAY_TEXT)
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                    property("overflow", "hidden")
                }
            }) {
                HtmlBlock(section.description) {
                    property("width", "100%")
                    property("max-width", "100%")
                    property("min-width", "0")
                    property("font-size", "10px")
                    property("color", C5_GRAY_TEXT)
                    property("line-height", "1.5")
                    property("white-space", "normal")
                    property("overflow-wrap", "anywhere")
                    property("word-break", "break-word")
                }
            }
        }
    }
}